import {getAuth} from 'firebase/auth';
import geojson from 'geojson';
import * as I from 'immutable';

import {
  AgentSceneMode,
  ApiExploreRunScene,
  ExploreRunPrompt,
} from 'app/components/AgentExplorer/types';
import {NoteGraph} from 'app/components/AnalyzePolygonChart/types';
import {
  DashboardConfig,
  FeatureMetadataApiResponse,
  FeatureStatsApiResponse,
  MultifeatureAnalysisConfig,
} from 'app/components/Dashboard/types';
import {ShareLinkScene} from 'app/components/PublicMap';
import {ApiShareLinkConfig} from 'app/components/PublicMap/PublicShareView';
import {ApiRecentRun} from 'app/modules/AgentRuns/usePendingAgentRuns';
import type {ApiSavedPrompt, SavedPromptDraft} from 'app/modules/SavedPrompts/types';
import {
  AgentWorkflowRunsResponse,
  ApiAgentRun,
  ApiAgentRunMetadata,
  ApiAgentWorkflow,
} from 'app/pages/Agent/types';
import {ManagePropertiesMode} from 'app/pages/ManageProperties';
import * as apiUtils from 'app/utils/apiUtils';
import * as CONSTANTS from 'app/utils/constants';
import {CameraOptions} from 'app/utils/mapUtils';
import {OverlayConfig} from 'app/utils/overlayUtils';

import {ApiLensConvertFeatureFile, ApiLensConvertFeatureUpdateFile} from './Admin';
import {
  APIAlertPolicyProperties,
  AlertPolicy,
  ApiAlertEnrollment,
  ApiAlertPolicy,
  ApiBulkOrderResponse,
  ApiBulkOrderVerificationResponse,
  ApiFeature,
  ApiFeatureData,
  ApiLensImageryRecord,
  ApiNote,
  ApiOrderableScene,
  ApiProjectReport,
  ApiReport,
  ApiReportBatchUpdateResponse,
  ApiReportExportRequest,
  ReportState,
} from './Feature';
import {
  ApiAlertCountsByFeatureId,
  ApiFeatureCollection,
  ApiNoteCountsByFeatureId,
} from './FeatureCollection';
import {ApiImageryPrices} from './Imagery';
import {
  ApiAccessSubscription,
  ApiBillingSessionResponse,
  ApiCardPurchase,
  ApiCardPurchaseInvoice,
  ApiDefaultPaymentMethod,
  ApiImageryPrepaid,
  ApiInvoice,
  ApiListPremiumSourcesResponse,
  ApiNewCustomerSignupResponse,
  ApiNotification,
  ApiNotificationPref,
  ApiNotificationScope,
  ApiNotificationType,
  ApiOrganization,
  ApiOrganizationUser,
  ApiOverlay,
  ApiPaymentMethod,
  ApiSignupToken,
  ApiStripeClientSecret,
  ApiUserRoleValue,
} from './Organization';
import {
  ApiImageryContract,
  ApiImageryContractWithProjects,
  ApiImageryPurchase,
  ApiImageryPurchaseBillingRecord,
  ApiNewProjectFeatureCollection,
  ApiProject,
} from './Project';
import {ApiShareLink, ApiShareLinkDeleteResponse} from './ShareLink';
import {ApiTaskingPlan, ApiTaskingVendor} from './Tasking';

export type FeaturesApi = ReturnType<typeof _makeFeaturesApi>;

export type {OverlayConfig, OverlayStyle} from 'app/utils/overlayUtils';

export interface ApiPublicLensResponse {
  features: ApiFeature[];
  overlay_feature_collections: ApiFeatureCollection[];
  overlay_configs: OverlayConfig[];
  data: ApiFeatureData[];
  scenes: ShareLinkScene[];
  description: string;
  note_geometry: geojson.Geometry | null;
  attachments: string[] | null;
  graph: NoteGraph | null;
  mapOptions: CameraOptions | null;
  /** Present only on report share links, which the /p/r/:uuid and /p/s/:uuid pages render. */
  report?: ApiPublicReportMeta;
  organization?: ApiPublicOrganization;
  feature_collection?: ApiPublicFeatureCollection;
}

export interface ApiPublicReportMeta {
  id: number;
  featureId: number;
  featureCollectionId: number;
  title: string;
  isMultilocation: boolean;
  /** Which page the link was made for; each public page renders only its own. */
  presentation: ReportPresentation;
}

export type ReportPresentation = 'document' | 'story';

/**
 * The trimmed org, feature collection, and note a report share link serves. They live
 * beside ApiPublicLensResponse so drift from the full types stays visible: the public
 * routes build these by allowlist, so a field the renderer starts reading has to be
 * added on the backend too.
 */
export interface ApiPublicOrganization {
  id: string;
  name: string;
  settings: Pick<ApiOrganization['settings'], 'logo' | 'display_area_unit' | 'note_tags'>;
}

export interface ApiPublicFeatureCollection {
  id: number;
  name: string;
  tiles: ApiFeatureCollection['tiles'];
  processingConfig: Pick<ApiFeatureCollection['processingConfig'], 'enrolledLayers'>;
}

export type ApiPublicNote = Pick<
  ApiNote,
  | 'id'
  | 'displayNumber'
  | 'featureId'
  | 'featureCollectionId'
  | 'noteType'
  | 'createdAt'
  | 'updatedAt'
  | 'text'
  | 'geometry'
  | 'attachments'
  | 'graph'
  | 'tagIds'
  | 'overlayFeatureCollectionIds'
  | 'layerKey'
  | 'cursorKey'
  | 'cursorType'
  | 'userName'
  | 'isArchived'
>;

export interface ApiPublicReport {
  id: number;
  title: string;
  isMultilocation: boolean;
  data: ReportState;
}

export interface StartWorkflowRunOptions {
  prompt?: string;
  reasoningEffort?: string;
  useFewShotExamples?: boolean;
  featureIdToDateRanges?: Record<number, [[string, string], [string, string]]>;
}

export interface ApiSuggestedLayer {
  layerKey: string;
  // Why this layer helps with the prompt the user submitted.
  reason: string;
}

export interface AgentRunPlanParams {
  featureIds: number[];
  title: string;
  sceneMode: AgentSceneMode;
  // Imagery windows, MM/DD/YYYY, stored on the plan so every run of it reads the same scenes.
  // A compare plan sends both or neither; a single-scene plan sends afterDateRange alone.
  beforeDateRange?: [string, string];
  afterDateRange?: [string, string];
  // Coarsest imagery (m/px) the plan's runs may read.
  maxSceneResolution?: number;
}

// The prompt half is a preset key or custom free text, never both, and resolves server-side
// against sceneMode -- the same contract a single-feature run uses.
export type MakeWorkflowParams = {organizationId: string} & AgentRunPlanParams & ExploreRunPrompt;

/**
 * An organization's imagery spend cap for its current term. When an organization has one, it
 * replaces each contract's NTE.
 */
export interface ApiImageryBudget {
  /** Null is no cap. */
  cents: number | null;
  /** ISO8601; null when no term is current. */
  startDate: string | null;
  /** ISO8601; null when the term is open-ended or none is current. */
  endDate: string | null;
  /** Pre-tax spend across the organization's portfolios within the term. */
  spentCents: number;
}

/**
 * Which flow submitted a checkout. The server can't infer it — a cart holding one scene, and a
 * cart split per contract into one-item calls, both look like a buy-now.
 */
export type ApiImageryCheckoutOrigin = 'buyNow' | 'cart';

/** One scene in a checkout. Every field but `geometry` is required; a 400 comes back otherwise. */
export interface ApiImageryCheckoutItem {
  projectId: string;
  featureCollectionId: number;
  featureId: number;
  sourceId: string;
  sensingTime: string;
  centsPerAcre: number;
  billableAcres: number;
  /** Partial-AOI sub-area. Omitted means the whole feature geometry. */
  geometry?: geojson.Polygon | geojson.MultiPolygon;
}

export interface ApiImageryCheckoutOrderedItem {
  featureId: number;
  sourceId: string;
  sensingTime: string;
  purchaseId: number;
  billedAcres: number;
  /**
   * The per-acre price, NOT the line total — it is the billing record's `price_cents`, and
   * `totalCents` sums `priceCents * billedAcres`. Render it through `orderedItemPriceCents`.
   */
  priceCents: number;
  needsManualKickoff: boolean;
}

/**
 * The outcome of the one charge attempt the checkout makes. Nothing is ordered unless it comes back
 * `paid` or `processing`, a bank debit Stripe has accepted that settles in a few business days:
 * `failed` is a decline, `requiresAction` needs a 3DS challenge, and `pending` means our call never
 * reached a verdict. `requiresAction` and `pending` are finished by resending the same
 * `checkoutKey`. `failed` is terminal for that key — trying another card needs a new `checkoutKey`.
 * A checkout sent without a card comes back `pending` when its invoice still has something to pay,
 * and resending its `checkoutKey` with a card finishes it.
 */
export type ApiImageryCheckoutPayment =
  | {status: 'paid'}
  | {status: 'processing'}
  | {status: 'requiresAction'; clientSecret: string}
  | {status: 'failed'; failureMessage: string}
  | {status: 'pending'; failureMessage: string};

/** What `preview` prices an item by. */
export interface ApiOrderableSceneKey {
  featureId: number;
  sourceId: string;
  sensingTime: string;
}

export type ApiImageryCheckoutPreviewItem = Pick<
  ApiImageryCheckoutItem,
  'projectId' | 'centsPerAcre' | 'billableAcres'
>;

export interface ApiImageryCheckoutPreview {
  orderFeeCents: number;
  /** What tops the scenes up to `orderMinimumThresholdCents`; zero when they already reach it. */
  orderMinimumCents: number;
  orderMinimumThresholdCents: number;
  /**
   * Prepaid credit the checkout would apply to the scenes, before tax, drawn from what's left right
   * now. `totalCents` already takes it off. A server from before credit sends none.
   */
  prepaidCents?: number;
  taxCents: number;
  /** The items plus `orderMinimumCents`, `orderFeeCents` and `taxCents`, less any discount. */
  totalCents: number;
}

export interface ApiImageryCheckoutInvoice {
  id: string;
  /**
   * Which Stripe account the invoice lives in, so a `requiresAction` clientSecret is confirmed
   * against the matching publishable key. Test mode is per-organization, so a prod build can be
   * holding a test-mode secret.
   */
  stripeMode: 'test' | 'live';
  // Null on an invoice that never finalized, and on one Stripe would not hand back after the charge.
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  /**
   * What the buyer is actually charged: `totalCents` plus `orderMinimumCents` and `orderFeeCents`,
   * plus `taxCents`, which Stripe adds at finalization.
   */
  amountDueCents: number;
  taxCents: number;
  // Zero on an invoice that had nothing due before the fee.
  orderFeeCents: number;
  // Zero when the scenes already reached the order minimum.
  orderMinimumCents: number;
  payment: ApiImageryCheckoutPayment;
}

/** Pays for a checkout on its own invoice, by card or prepaid credit, instead of its contract. */
export interface ApiImageryCheckoutOptions {
  /**
   * A `ctoken_…` from `stripe.createConfirmationToken`: the card, and whether to save it. Left out
   * when prepaid credit covers the whole checkout, which then charges no card.
   */
  confirmationTokenId?: string;
  checkoutKey: string;
  /**
   * Printed on the checkout invoice as a "Billing note" custom field, at most 140 characters. It is
   * among the params behind the invoice's idempotency key, so a retry of `checkoutKey` has to send
   * the very same note or the checkout can't be resumed.
   */
  note?: string;
}

export interface ApiImageryCheckoutResult {
  items: ApiImageryCheckoutOrderedItem[];
  totalCents: number;
  totalBilledAcres: number;
  /**
   * Prepaid credit the order drew, before tax, on an order that went through. A server from before
   * it reported this sends none.
   */
  prepaidCents?: number;
  invoice?: ApiImageryCheckoutInvoice;
  resumed: boolean;
}

export interface ApiPrepaidPurchasePreview {
  orderFeeCents: number;
  taxCents: number;
  /** The credit plus `orderFeeCents` and `taxCents`: what the card is charged. */
  totalCents: number;
}

export interface ApiPrepaidPurchaseResult {
  creditCents: number;
  /**
   * Whether the credit is in the balance already. A paid card purchase is granted right away; a bank
   * debit is granted once it settles, and a paid purchase the server couldn't record is granted
   * moments later from Stripe.
   */
  granted: boolean;
  invoice: ApiImageryCheckoutInvoice;
}

export type ApiImageryCheckoutItemErrorCode =
  | 'sourceNotConfigured'
  | 'invalidSource'
  | 'invalidScene'
  | 'alreadyPurchased'
  | 'priceChanged'
  | 'acresMismatch'
  | 'sourceDisabled'
  | 'duplicateInCart'
  | 'notFound'
  | 'limitReached';

/**
 * A per-item failure. Rejections identify the scene rather than echoing a client id, so matching
 * one back to a submitted item means comparing the scene tuple.
 */
export interface ApiImageryCheckoutItemError {
  featureId: number;
  sourceId: string;
  sensingTime: string;
  code: ApiImageryCheckoutItemErrorCode;
  message: string;
  /** The current price, on `priceChanged` only. */
  centsPerAcre?: number;
  /** The scene's true billable acres, on `acresMismatch` only. */
  billableAcres?: number;
}

export default {
  projects: _makeProjectsApi(),
  featureCollections: _makeFeatureCollectionsApi(),
  // features is not present here because it is a child of featureCollections
  organizations: _makeOrganizationsApi(),
  public: Object.assign(
    (uuid: string) =>
      apiUtils.makeApiRequest<ApiPublicLensResponse>(`p/${uuid}`, null, undefined, {
        highVolumeApi: true,
      }),
    // The routes a public report page loads from. Auth headers are automatic on share-link
    // pages, where getAuthHeaders sends the link's uuid as the credential. Overlay geometry
    // isn't among them: overlays render from their feature collection's PMTiles.
    {
      report: (uuid: string) => apiUtils.makeApiRequest<ApiPublicReport>(`p/${uuid}/report`),

      featureNotes: (
        uuid: string,
        featureId: number,
        params: apiUtils.ApiParams = {},
        options: apiUtils.ListOptions = {getAllPages: true}
      ) =>
        makeRequestWithPages(
          (p: apiUtils.ApiParams) =>
            apiUtils.makeApiRequest<ApiPublicNote[]>(`p/${uuid}/features/${featureId}/notes`, p),
          params,
          options
        ),

      featureData: (
        uuid: string,
        featureId: number,
        params: apiUtils.ApiParams = {},
        options: apiUtils.ListOptions = {getAllPages: true}
      ) =>
        makeRequestWithPages(
          (p: apiUtils.ApiParams) =>
            apiUtils.makeApiRequest<ApiFeatureData[]>(`p/${uuid}/features/${featureId}/data`, p),
          params,
          options
        ),
    }
  ),
  publicWidget: (uuid: string, apiParams: apiUtils.ApiParams = {perPage: 100}) => {
    const request = (p: apiUtils.ApiParams) =>
      apiUtils.makeApiRequest<FeatureStatsApiResponse[]>(`p/w/${uuid}`, p, {
        method: 'GET',
      });
    return makeRequestWithPages(request, apiParams, {getAllPages: true});
  },
  makeShareLink: (shareDetails: ApiShareLinkConfig) =>
    apiUtils.makeApiRequest<{id: string}>('p/share', null, {
      method: 'POST',
      body: JSON.stringify(shareDetails),
    }),
  reports: (featureId: number) => _makeReportsApi(featureId),
  projectReports: (projectId: string) => _makeProjectReportsApi(projectId),
  shareLinks: (orgId: string) => _makeShareLinksApi(orgId),
  dashboardStats: (orgId: string) => _makeDashboardStatsApi(orgId),
  searchFeatureCollections: (query: string) =>
    apiUtils.makeApiRequest<ApiFeatureCollection[]>(`search/featureCollections?query=${query}`),
  searchFeatures: (query: string, activeLensOnly = false, featureCollectionIds: number[] = []) => {
    let queryString = `?query=${query}&activeLensOnly=${activeLensOnly}`;
    if (featureCollectionIds) {
      queryString += `&featureCollectionId=${featureCollectionIds.join('&featureCollectionId=')}`;
    }
    return apiUtils.makeApiRequest<ApiFeature[]>(`search/features${queryString}`);
  },
  searchProjects: (query: string) =>
    apiUtils.makeApiRequest<ApiProject[]>(`search/projects?query=${query}`),
  user: {
    fetch: () => apiUtils.makeApiRequest<ApiOrganizationUser>('user'),
  },

  agent: {
    getWorkflowRun: (workflowId, runId: string) =>
      apiUtils.makeApiRequest<ApiAgentRun>(`agent/workflow/${workflowId}/run/${runId}`),
    getWorkflowRuns: (workflowId: string, params: apiUtils.ApiParams = {}) => {
      return apiUtils.makeApiRequest<AgentWorkflowRunsResponse>(
        `agent/workflow/${workflowId}`,
        params
      );
    },
    startWorkflowRun: (workflowId: string, options?: StartWorkflowRunOptions) =>
      apiUtils.makeApiRequest<ApiAgentRun>(`agent/workflow/${workflowId}/run`, null, {
        method: 'POST',
        body: JSON.stringify(options || {}),
      }),
    makeWorkflow: (workflow: MakeWorkflowParams) =>
      apiUtils.makeApiRequest<ApiAgentWorkflow>(`agent/workflow`, null, {
        method: 'POST',
        body: JSON.stringify(workflow),
      }),
    // Archives the plan along with every run of it, cancelling anything still in flight.
    archiveWorkflow: (workflowId: string) =>
      apiUtils.makeApiRequest<ApiAgentWorkflow>(`agent/workflow/${workflowId}`, null, {
        method: 'DELETE',
      }),
    archiveWorkflowRun: (workflowId: string, runId: number) =>
      apiUtils.makeApiRequest<ApiAgentRunMetadata>(
        `agent/workflow/${workflowId}/run/${runId}`,
        null,
        {
          method: 'DELETE',
        }
      ),
    startExploreRun: (featureId: number, scenes: ApiExploreRunScene[], prompt: ExploreRunPrompt) =>
      apiUtils.makeApiRequest<
        {
          featureId: number;
          scenes: ApiExploreRunScene[];
        } & ExploreRunPrompt
      >(`agent/${featureId}/run`, null, {
        method: 'POST',
        body: JSON.stringify({featureId, scenes, ...prompt}),
      }),
    getExploreRuns: (featureId: number) =>
      apiUtils.makeApiRequest<{
        featureId: number;
      }>(`agent/${featureId}/runs`, null, {
        method: 'GET',
      }),
    getRecentRuns: () =>
      apiUtils.makeApiRequest<ApiRecentRun[]>(`agent/runs/recent`, null, {
        method: 'GET',
      }),
    archiveExploreRun: (agentRunId: number) =>
      apiUtils.makeApiRequest<void>(`agent/run/${agentRunId}`, null, {
        method: 'DELETE',
      }),
    getRunLogs: (agentRunId: number) =>
      apiUtils.makeApiRequest<{logs: string}>(`agent/run/${agentRunId}/logs`),
    // Generation regularly runs for minutes, well past the 30s api-server
    // timeout, so this has to go through slow-api.
    generatePortfolio: (prompt: string) =>
      apiUtils.makeApiRequest<{featureCollection: geojson.FeatureCollection}>(
        'agent/generate_portfolio',
        null,
        {method: 'POST', body: JSON.stringify({prompt})},
        {slowApi: true}
      ),
    // Runs alongside generatePortfolio, so it goes through slow-api too rather than risk the
    // 30s api-server timeout cutting it off.
    suggestLayers: (prompt: string, organizationId: string) =>
      apiUtils.makeApiRequest<{suggestions: ApiSuggestedLayer[]}>(
        'agent/suggest_layers',
        null,
        {method: 'POST', body: JSON.stringify({prompt, organizationId})},
        {slowApi: true}
      ),
    submitRunFeedback: (agentRunId: number, helpful: boolean, comment?: string) =>
      apiUtils.makeApiRequest<{success: boolean}>(`agent/run/${agentRunId}/feedback`, null, {
        method: 'POST',
        body: JSON.stringify({helpful, comment}),
      }),
    // Owner-only toggle for the org's agent settings. Only `enabled`/`hidden` may be set here;
    // `run_quota` is Upstream-controlled. Returns the updated organization.
    updateOrgAgentSettings: (orgId: string, settings: {enabled?: boolean; hidden?: boolean}) =>
      apiUtils.makeApiRequest<ApiOrganization>(`agent/organization/${orgId}/settings`, null, {
        method: 'PATCH',
        body: JSON.stringify(settings),
      }),
    // Saved prompts, shown in the app as automations. Staff-only for now.
    listPrompts: (params: apiUtils.ApiParams = {}) =>
      apiUtils.makeApiRequest<ApiSavedPrompt[]>('agent/prompts', params),
    createPrompt: (prompt: SavedPromptDraft & {organizationId: string}) =>
      apiUtils.makeApiRequest<ApiSavedPrompt>('agent/prompts', null, {
        method: 'POST',
        body: JSON.stringify(prompt),
      }),
    archivePrompt: (promptId: number) =>
      apiUtils.makeApiRequest<ApiSavedPrompt>(`agent/prompts/${promptId}`, null, {
        method: 'DELETE',
      }),
    restorePrompt: (promptId: number) =>
      apiUtils.makeApiRequest<ApiSavedPrompt>(`agent/prompts/${promptId}/restore`, null, {
        method: 'POST',
      }),
  },

  signup: {
    fetch: (token: string) => apiUtils.makeApiRequest<ApiSignupToken>(`signup/${token}`),
    accept: (token: string, args: {name: string}) =>
      apiUtils.makeApiRequest<void>(`signup/${token}/accept`, null, {
        method: 'POST',
        body: JSON.stringify(args),
      }),
    subscribe: (
      organizationName: string,
      accountEmail: string,
      subscriptionType: string,
      subscriptionAddOns: string[],
      taxExempt: boolean,
      billingAddress: object,
      freeTrial: string | undefined,
      isTest: boolean
    ) =>
      apiUtils.makeApiRequest<ApiNewCustomerSignupResponse>(`signup/lens/subscribe`, null, {
        method: 'POST',
        body: JSON.stringify({
          organizationName,
          accountEmail,
          subscriptionType,
          subscriptionAddOns,
          taxExempt,
          billingAddress,
          isTest,
          ...(freeTrial && {freeTrial}),
        }),
      }),
  },

  prefs: {
    notifications: {
      fetch: (params: apiUtils.ApiParams = {}) => {
        const request = (p: apiUtils.ApiParams) =>
          apiUtils.makeApiRequest<ApiNotificationPref[]>(`prefs/notifications`, p);
        return makeRequestWithPages(request, params, {getAllPages: true});
      },
      set: (
        projectId: string,
        notificationType: ApiNotificationType,
        scope: ApiNotificationScope
      ) =>
        apiUtils.makeApiRequest<ApiNotificationPref>(
          `prefs/notifications/${projectId}/${notificationType}`,
          null,
          {
            method: 'PUT',
            body: JSON.stringify({scope}),
          }
        ),
    },
  },

  imagery: {
    purchases: {
      fetch(
        contractId: number,
        params: apiUtils.ApiParams = {},
        options: apiUtils.ListOptions = {}
      ) {
        const request = (p: apiUtils.ApiParams) =>
          apiUtils.makeApiRequest<ApiImageryPurchase[]>(
            `imagery/imagery_contracts/${contractId}/purchases`,
            p,
            {
              method: 'GET',
            }
          );

        return makeRequestWithPages(request, params, options);
      },

      fetchGen(
        contractId: number,
        params: apiUtils.ApiParams = {},
        options: apiUtils.ListOptions = {}
      ) {
        const request = (p: apiUtils.ApiParams) =>
          apiUtils.makeApiRequest<ApiImageryPurchase[]>(
            `imagery/imagery_contracts/${contractId}/purchases`,
            p,
            {method: 'GET'}
          );

        return apiUtils.collectPages(request, params, options);
      },
    },
    billingRecords: {
      fetchGen(
        contractId: number,
        params: apiUtils.ApiParams = {},
        options: apiUtils.ListOptions = {}
      ) {
        const request = (p: apiUtils.ApiParams) =>
          apiUtils.makeApiRequest<ApiImageryPurchaseBillingRecord[]>(
            `imagery/imagery_contracts/${contractId}/billingRecords`,
            p,
            {method: 'GET'}
          );

        return apiUtils.collectPages(request, params, options);
      },
      fetch(
        contractId: number,
        params: apiUtils.ApiParams = {},
        options: apiUtils.ListOptions = {}
      ) {
        const request = (p: apiUtils.ApiParams) =>
          apiUtils.makeApiRequest<ApiImageryPurchaseBillingRecord[]>(
            `imagery/imagery_contracts/${contractId}/billingRecords`,
            p,
            {method: 'GET'}
          );

        return makeRequestWithPages(request, params, options);
      },
      /** Finds records with no contract too, which an org that pays at checkout makes. */
      fetchForOrganization(
        organizationId: string,
        params: apiUtils.ApiParams = {},
        options: apiUtils.ListOptions = {}
      ) {
        const request = (p: apiUtils.ApiParams) =>
          apiUtils.makeApiRequest<ApiImageryPurchaseBillingRecord[]>(
            `imagery/organizations/${organizationId}/billingRecords`,
            p,
            {method: 'GET'}
          );

        return makeRequestWithPages(request, params, options);
      },
    },
    prices: {
      fetch: () =>
        apiUtils.makeApiRequest<ApiImageryPrices[]>(`imagery/imageryPrices`, null, {
          method: 'GET',
        }),
    },
    orderableScenes: {
      /**
       * The scene browser's listing of exactly these scenes, in one request however many properties
       * they span. A scene the browser wouldn't list is left out of the answer.
       */
      lookUp: (scenes: ApiOrderableSceneKey[]) =>
        apiUtils.makeApiRequest<(ApiOrderableScene & {featureId: number})[]>(
          `imagery/orderableScenes`,
          null,
          {method: 'POST', body: JSON.stringify({scenes})}
        ),
    },
    checkouts: {
      /**
       * Orders a batch of scenes all-or-nothing. Every item has to bill to one imagery contract, so
       * a cart spanning contracts submits one call per group. Passing `options` charges a one-off
       * Stripe invoice first: nothing is ordered unless `invoice.payment` comes back `paid`, or
       * `processing` for a bank debit.
       */
      create: async (
        items: ApiImageryCheckoutItem[],
        origin: ApiImageryCheckoutOrigin,
        options?: ApiImageryCheckoutOptions
      ): Promise<ApiImageryCheckoutResult> => {
        const response = await apiUtils.makeApiRequest<ApiImageryCheckoutResult>(
          `imagery/checkouts`,
          null,
          {method: 'POST', body: JSON.stringify({items, origin, ...options})},
          {slowApi: true}
        );
        return response.get('data').toJS() as ApiImageryCheckoutResult;
      },
      /** What `create` would charge a card for these items, which have to share one contract. */
      preview: async (
        items: ApiImageryCheckoutPreviewItem[]
      ): Promise<ApiImageryCheckoutPreview> => {
        const response = await apiUtils.makeApiRequest<ApiImageryCheckoutPreview>(
          `imagery/checkouts/preview`,
          null,
          {method: 'POST', body: JSON.stringify({items})}
        );
        return response.get('data').toJS() as ApiImageryCheckoutPreview;
      },
    },
    prepaidPurchases: {
      /**
       * Buys prepaid credit on a one-off invoice. Like a checkout, resending the same `checkoutKey`
       * and token finishes a 3DS challenge or an unconfirmed charge instead of charging again.
       */
      create: async (
        cents: number,
        checkoutKey: string,
        confirmationTokenId: string
      ): Promise<ApiPrepaidPurchaseResult> => {
        const response = await apiUtils.makeApiRequest<ApiPrepaidPurchaseResult>(
          `imagery/prepaidPurchases`,
          null,
          {method: 'POST', body: JSON.stringify({cents, checkoutKey, confirmationTokenId})},
          {slowApi: true}
        );
        return response.get('data').toJS() as ApiPrepaidPurchaseResult;
      },
      preview: async (cents: number): Promise<ApiPrepaidPurchasePreview> => {
        const response = await apiUtils.makeApiRequest<ApiPrepaidPurchasePreview>(
          `imagery/prepaidPurchases/preview`,
          null,
          {method: 'POST', body: JSON.stringify({cents})}
        );
        return response.get('data').toJS() as ApiPrepaidPurchasePreview;
      },
    },
  },

  tasking: {
    optimizePlan: (featureIds: number[], vendor: ApiTaskingVendor) =>
      /** Conversion hack here because makeApiRequest returns a value that
       * assumes that the data is in a "data" property, when it's actually just
       * the ApiTaskingPlan itself. */
      apiUtils.makeApiRequest<ApiTaskingPlan>(
        `tasking/optimizePlan`,
        null,
        {
          method: 'POST',
          body: JSON.stringify({featureIds, vendor}),
        },
        {slowApi: true}
      ) as unknown as Promise<I.ImmutableOf<ApiTaskingPlan>>,

    submitPlan: (
      plan: ApiTaskingPlan,
      startDate: string,
      endDate: string,
      spectralBands: string,
      additionalInfo: string,
      signatoryInfo: string,
      planCost: string
    ) =>
      apiUtils.makeApiRequest<void>(`tasking/submitPlan`, null, {
        method: 'POST',
        body: JSON.stringify({
          plan,
          startDate,
          endDate,
          spectralBands,
          additionalInfo,
          signatoryInfo,
          planCost,
        }),
      }),
  },

  lens: {
    uploader: {
      convertFile: (file: File, uploadOperation?: 'verify' | 'upload' | 'order') => {
        const formData = new FormData();
        formData.append('file', file);
        if (uploadOperation) {
          formData.append('upload_operation', uploadOperation);
        }

        return apiUtils.makeApiRequest<ApiLensConvertFeatureFile>(
          'lens/uploader/convertToGeoJSON',
          null,
          {
            method: 'POST',
            body: formData,
          },
          {slowApi: true}
        );
      },
      convertOverlay: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        return apiUtils.makeApiRequest<ApiLensConvertFeatureFile>(
          'lens/uploader/convertOverlayToGeoJSON',
          null,
          {
            method: 'POST',
            body: formData,
          },
          {slowApi: true}
        );
      },
      convertUpdateFile: (file: File, fcId: number, nameProp?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('feature_collection_id', fcId.toString());
        if (nameProp) {
          formData.append('name_prop', nameProp);
        }

        return apiUtils.makeApiRequest<ApiLensConvertFeatureUpdateFile>(
          'lens/uploader/previewPropertyUpdate',
          null,
          {
            method: 'POST',
            body: formData,
          },
          {slowApi: true}
        );
      },
      submitUpdates: (
        fcId: number,
        gcsFilePath: string,
        matchedUpdates: Record<number, number>,
        newFeatures: number[],
        nameEdits: Record<number, string>,
        partNameEdits: Record<number, string>
      ) => {
        return apiUtils.makeApiRequest<{migrated_feature_ids: string[]; problems: string[]}>(
          'lens/uploader/submitPropertyUpdate',
          null,
          {
            method: 'POST',
            body: JSON.stringify({
              gcs_shapefile_path: gcsFilePath,
              feature_collection_id: fcId,
              matched_updates: matchedUpdates,
              new_features: newFeatures,
              name_edits: nameEdits,
              part_name_edits: partNameEdits,
            }),
          }
        );
      },
    },
  },
};

////////////////////////////////////////////////////////////////////////////////
// PROJECTS
////////////////////////////////////////////////////////////////////////////////

function _makeProjectsApi() {
  return {
    ...apiUtils.createUpdateApiResource<ApiProject, string>('projects'),

    create(
      organizationId: string,
      name: string,
      product: string,
      layersBySource: ApiOrganization['defaultLayersBySource']
    ) {
      return apiUtils.makeApiRequest<ApiNewProjectFeatureCollection>(`projects`, null, {
        method: 'POST',
        body: JSON.stringify({organizationId, name, product, layersBySource}),
      });
    },

    delete(projectId: string) {
      return apiUtils.makeApiRequest<apiUtils.NeverResponse>(`projects/${projectId}`, null, {
        method: 'DELETE',
      });
    },

    putFeatureCollectionIds(projectId: string, featureCollectionIds: number[]) {
      return apiUtils.makeApiRequest<ApiProject>(
        `projects/${projectId}/featureCollectionIds`,
        null,
        {
          method: 'PUT',
          body: JSON.stringify(featureCollectionIds),
        }
      );
    },

    currentImageryContract(projectId: string) {
      return apiUtils.makeApiRequest<ApiImageryContract | null>(
        `projects/${projectId}/imagery_contracts/current`
      );
    },

    addOverlay(projectId: string, featureCollectionId: number) {
      return apiUtils.makeApiRequest<ApiProject>(`projects/${projectId}/addOverlay`, null, {
        method: 'POST',
        body: JSON.stringify({feature_collection_id: featureCollectionId}),
      });
    },

    removeOverlay(projectId: string, featureCollectionId: number) {
      return apiUtils.makeApiRequest<ApiProject>(`projects/${projectId}/removeOverlay`, null, {
        method: 'POST',
        body: JSON.stringify({feature_collection_id: featureCollectionId}),
      });
    },
  };
}

////////////////////////////////////////////////////////////////////////////////
// FEATURE COLLECTIONS
////////////////////////////////////////////////////////////////////////////////

function _makeFeatureCollectionsApi() {
  const {
    list,
    fetch: fetchFc,
    update,
  } = apiUtils._createApiResource<ApiFeatureCollection, number>('featureCollections');

  return {
    // list just used for api tests
    list,
    fetch: fetchFc,
    update,

    batchFetch(params: apiUtils.ApiParams) {
      return apiUtils.makeApiRequest<ApiFeatureCollection[]>('featureCollections/batch', params);
    },

    listStyleableAttributes(featureCollectionId: number) {
      return apiUtils.makeApiRequest<{name: string; values: string[]}[]>(
        `featureCollections/${featureCollectionId}/styleableAttributes`
      );
    },

    features(fcId: number) {
      return _makeFeaturesApi(fcId);
    },

    uploadFeaturesFromFile(
      fcId: number,
      gcsFilePath: string,
      nameEdits: Record<number, string>,
      partNameEdits: Record<number, string | null> | null,
      excludedFeatureIds: number[],
      mode: ManagePropertiesMode,
      params: apiUtils.ApiParams = {}
    ) {
      return apiUtils.makeApiRequest<void>(`featureCollections/${fcId}/upload_features`, params, {
        method: 'POST',
        body: JSON.stringify({
          gcs_shapefile_path: gcsFilePath,
          name_props: [],
          part_name_prop: '',
          name_edits: nameEdits,
          part_name_edits: partNameEdits,
          drop_feature_indexes: excludedFeatureIds,
          mode: mode,
        }),
      });
    },

    uploadOverlay(
      name: string,
      gcsFilePath: string,
      nameEdits: Record<number, string>,
      excludedFeatureIds: number[],
      hoverProperties: {key: string; label: string}[] = [],
      params: apiUtils.ApiParams = {},
      allowEmptyName = false
    ) {
      return apiUtils.makeApiRequest<ApiFeatureCollection>(`featureCollections/overlay`, params, {
        method: 'POST',
        body: JSON.stringify({
          name: name,
          gcs_shapefile_path: gcsFilePath,
          name_props: [],
          part_name_prop: '',
          name_edits: nameEdits,
          part_name_edits: {}, //the backend expects this field, but we're not sending anything from the frontend
          drop_feature_indexes: excludedFeatureIds,
          hover_properties: hoverProperties,
          allow_empty_name: allowEmptyName, // if true, the backend will not supply a default "Feature 1" name to overlay features
        }),
      });
    },

    updateOverlay(
      overlayFeatureCollectionId: number,
      gcsFilePath: string,
      nameEdits: Record<number, string>,
      excludedFeatureIds: number[],
      hoverProperties: {key: string; label: string}[] = []
    ) {
      return apiUtils.makeApiRequest<ApiFeatureCollection>(
        `featureCollections/${overlayFeatureCollectionId}/overlay`,
        null,
        {
          method: 'POST',
          body: JSON.stringify({
            gcs_shapefile_path: gcsFilePath,
            name_props: [],
            part_name_prop: '',
            name_edits: nameEdits,
            part_name_edits: {},
            drop_feature_indexes: excludedFeatureIds,
            hover_properties: hoverProperties,
          }),
        }
      );
    },

    listImagerySummary(
      pId: string,
      fcId: number,
      year: number,
      params: apiUtils.ApiParams = {perPage: 500},
      options: apiUtils.ListOptions = {getAllPages: 2}
    ) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiLensImageryRecord[]>(
          `projects/${pId}/featureCollections/${fcId}/overview/imagery_summary/${year}`,
          p
        );

      return apiUtils.collectPages(request, params, options);
    },

    fetchNoteCountsByFeatureId(pId: string, fcId: number, noteType: ApiNote['noteType']) {
      return apiUtils.makeApiRequest<ApiNoteCountsByFeatureId>(
        `projects/${pId}/featureCollections/${fcId}/overview/noteCounts/${noteType}`
      );
    },

    fetchAlertCountsByFeatureId(pId: string, fcId: number) {
      return apiUtils.makeApiRequest<ApiAlertCountsByFeatureId>(
        `projects/${pId}/featureCollections/${fcId}/overview/alertCounts`
      );
    },

    fetchReportCountsByFeatureId(pId: string, fcId: number) {
      return apiUtils.makeApiRequest<I.Map<string, number>>(
        `projects/${pId}/featureCollections/${fcId}/overview/reportCounts`
      );
    },

    archiveFeatureCollection(fcId: number, params: apiUtils.ApiParams = {}) {
      return apiUtils.makeApiRequest<void>(`featureCollections/${fcId}`, params, {
        method: 'DELETE',
      });
    },

    addLayer(fcId: number, layers: Record<string, string[]>) {
      return apiUtils.makeApiRequest<ApiFeatureCollection>(
        `featureCollections/${fcId}/layers`,
        null,
        {
          method: 'POST',
          body: JSON.stringify({layers: layers}),
        }
      );
    },

    removeLayer(fcId: number, layers: Record<string, string[]>) {
      return apiUtils.makeApiRequest<ApiFeatureCollection>(
        `featureCollections/${fcId}/layers`,
        null,
        {
          method: 'DELETE',
          body: JSON.stringify({layers: layers}),
        }
      );
    },

    async export(fcId: number) {
      const auth = getAuth();
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        const route = apiUtils.getApiRoute(
          `featureCollections/${fcId}/export`,
          {
            apiToken: token,
            cOrgId: apiUtils.contextOrganizationId,
          },
          {slowApi: true}
        );
        // because the API properly sets the Content-Disposition attachment
        // header, we just need to open it to download!
        window.open(route);
      }
    },

    createAlertPolicy(
      params: Pick<
        AlertPolicy,
        'featureCollectionId' | 'sourceId' | 'layerId' | 'alertType' | 'description' | 'name'
      > & {
        properties: APIAlertPolicyProperties;
      }
    ) {
      const {featureCollectionId, sourceId, layerId, alertType, properties, name, description} =
        params;
      return apiUtils.makeApiRequest<ApiAlertPolicy>(
        `featureCollections/${featureCollectionId}/alertPolicies`,
        null,
        {
          method: 'POST',
          body: JSON.stringify({
            name: name,
            description: description,
            source_id: sourceId,
            layer_id: layerId,
            alert_type: alertType,
            properties: properties,
          }),
        }
      );
    },

    updateAlertPolicy(
      params: Pick<
        AlertPolicy,
        'featureCollectionId' | 'sourceId' | 'layerId' | 'alertType' | 'description' | 'name' | 'id'
      > & {
        properties: APIAlertPolicyProperties;
      }
    ) {
      const {featureCollectionId, sourceId, layerId, alertType, properties, name, description} =
        params;
      return apiUtils.makeApiRequest<ApiAlertPolicy>(
        `featureCollections/${featureCollectionId}/alertPolicies/${params.id}`,
        null,
        {
          method: 'PATCH',
          body: JSON.stringify({
            name: name,
            description: description,
            source_id: sourceId,
            layer_id: layerId,
            alert_type: alertType,
            properties: properties,
          }),
        }
      );
    },

    archiveAlertPolicy(featureCollectionId: number | string, alertPolicyId: number) {
      return apiUtils.makeApiRequest<ApiAlertPolicy>(
        `featureCollections/${featureCollectionId}/alertPolicies/${alertPolicyId}`,
        null,
        {
          method: 'PATCH',
          body: JSON.stringify({
            is_archived: true,
          }),
        }
      );
    },

    getAlertPolicies(featureCollectionId: number | string) {
      return apiUtils.makeApiRequest<ApiAlertPolicy[]>(
        `featureCollections/${featureCollectionId}/alertPolicies`,
        null,
        {method: 'GET'}
      );
    },

    getAlertPolicyEnrollments(featureCollectionId: string | number, alertPolicyId) {
      return apiUtils.makeApiRequest<ApiAlertEnrollment[]>(
        `featureCollections/${featureCollectionId}/alertPolicies/${alertPolicyId}/enrollments`,
        null,
        {method: 'GET'}
      );
    },

    enrollFeaturesInAlertPolicy(
      featureCollectionId: string | number,
      alertPolicyId: number,
      featureIds: number[]
    ) {
      return apiUtils.makeApiRequest<ApiAlertEnrollment[]>(
        `featureCollections/${featureCollectionId}/alertPolicies/${alertPolicyId}/enroll`,
        null,
        {
          method: 'POST',
          body: JSON.stringify({
            name: name,
            feature_ids: featureIds,
          }),
        }
      );
    },

    unenrollFeaturesInAlertPolicy(
      featureCollectionId: string | number,
      alertPolicyId: number,
      featureIds: number[]
    ): Promise<apiUtils.ApiResponse<{numDeletedCount: number}>> {
      return apiUtils.makeApiRequest<{numDeletedCount: number}>(
        `featureCollections/${featureCollectionId}/alertPolicies/${alertPolicyId}/unenroll`,
        null,
        {
          method: 'POST',
          body: JSON.stringify({
            name: name,
            feature_ids: featureIds,
          }),
        }
      );
    },
  };
}

////////////////////////////////////////////////////////////////////////////////
// FEATURES
////////////////////////////////////////////////////////////////////////////////

function _makeFeaturesApi(fcId: number) {
  const routeBase = `featureCollections/${fcId}/features`;
  return {
    ...apiUtils._createApiResource<ApiFeature, number>(`${routeBase}`),

    data(featureId: number, params: apiUtils.ApiParams = {}, options: apiUtils.ListOptions = {}) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiFeatureData[]>(`${routeBase}/${featureId}/data`, p);

      return makeRequestWithPages(request, params, options);
    },

    /**
     * Similar to data(), but yields streaming results before returning the
     * list of data.
     */
    dataGen(
      featureId: number,
      params: apiUtils.ApiParams = {perPage: 200},
      options: apiUtils.ListOptions = {getAllPages: 2}
    ) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiFeatureData[]>(`${routeBase}/${featureId}/data`, p);

      return apiUtils.collectPages(request, params, options);
    },

    batchFetch(params: apiUtils.ApiParams) {
      return apiUtils.makeApiRequest<ApiFeature[]>(`${routeBase}/batch`, params);
    },

    async parcelAlertEligibility(
      featureId: number
    ): Promise<{parcel_count: number; eligible: boolean}> {
      const response = await apiUtils.makeApiRequest<{
        parcel_count: number;
        eligible: boolean;
      }>(`${routeBase}/${featureId}/regridParcelEligibility`);
      return response.get('data').toJS();
    },

    batchPatch(
      id: I.List<number>,
      changes: I.MergesInto<I.MapAsRecord<I.ImmutableFields<ApiFeature>>>
    ) {
      return apiUtils.makeApiRequest<ApiFeature[]>(
        `${routeBase}/batch`,
        {id},
        {
          method: 'PATCH',
          body: JSON.stringify(changes),
        }
      );
    },

    batchArchive(id: I.List<number>) {
      return apiUtils.makeApiRequest<apiUtils.NeverResponse>(
        `${routeBase}/batch`,
        {id},
        {
          method: 'DELETE',
        }
      );
    },

    moveFeatures(featureCollectionId: number, projectId: string, featureIds: number[]) {
      return apiUtils.makeApiRequest<ApiFeature[]>(`${routeBase}/move_features`, null, {
        method: 'POST',
        body: JSON.stringify({
          feature_ids: featureIds,
          project_id: projectId,
          feature_collection_id: featureCollectionId,
        }),
      });
    },

    combineFeatures(name: string, featureIds: number[]) {
      return apiUtils.makeApiRequest<ApiFeature[]>(`${routeBase}/combine`, null, {
        method: 'POST',
        body: JSON.stringify({
          feature_ids: featureIds,
          name: name,
        }),
      });
    },

    verifyOrderImageryBulk(
      projectId: string,
      featureIds: number[],
      sensingTime: string | null,
      sourceId: string,
      sensingTimeMode?: 'all' | 'latest' | 'explicit'
    ): Promise<apiUtils.ApiResponse<ApiBulkOrderVerificationResponse>> {
      return apiUtils.makeApiRequest<ApiBulkOrderVerificationResponse>(
        `imagery/projects/${projectId}/featureCollections/${fcId}/bulk/cost`,
        null,
        {
          method: 'POST',
          body: JSON.stringify({
            featureIds,
            sensingTime,
            sourceId,
            sensingTimeMode,
          }),
        },
        {slowApi: true}
      );
    },
    orderImageryBulk(
      projectId: string,
      featureIds: number[],
      sensingTime: string | null,
      sourceId: string,
      expectedCostInCents: number,
      sensingTimeMode?: 'all' | 'latest' | 'explicit'
    ): Promise<apiUtils.ApiResponse<ApiBulkOrderResponse>> {
      return apiUtils.makeApiRequest<ApiBulkOrderResponse>(
        `imagery/projects/${projectId}/featureCollections/${fcId}/bulk/order`,
        null,
        {
          method: 'POST',
          body: JSON.stringify({
            featureIds,
            sensingTime,
            sourceId,
            billableCost: expectedCostInCents,
            sensingTimeMode,
          }),
        },
        {slowApi: true}
      );
    },
    orderableSourceIds(pId: string, fId: number) {
      return apiUtils.makeApiRequest<string[]>(
        `projects/${pId}/featureCollections/${fcId}/overview/${fId}/orderable_source_ids`
      );
    },
    orderableScenes(
      pId: string,
      fId: number,
      params: apiUtils.ApiParams = {},
      sourceIds?: string[]
    ) {
      const sourceParams: apiUtils.ApiParams = sourceIds?.length
        ? {...params, source_id: sourceIds}
        : params;
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiOrderableScene[]>(
          `projects/${pId}/featureCollections/${fcId}/overview/${fId}/paid_scenes`,
          p
        );

      // Source groupings often don't have more than 100 scenes,
      // so we only request 4 pages at a time to avoid making a bunch of requests for empty pages.
      return makeRequestWithPages(request, sourceParams, {getAllPages: 4});
    },
    async exportPng(
      fId: number,
      sourceId: string,
      layerKey: string,
      sensingTime: string,
      overlayConfigs?: OverlayConfig[]
    ) {
      const route = apiUtils.getApiRoute(
        `export/raster/${fId}/${sourceId}/${layerKey}/${sensingTime}.png`,
        null,
        {slowApi: true}
      );

      const auth = getAuth();
      const headers = new Headers();
      headers.append('Content-Type', 'application/json');
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        if (token) {
          headers.append('Authorization', token);
        }
        if (apiUtils.contextOrganizationId) {
          headers.append('x-context-organization-id', apiUtils.contextOrganizationId);
        }
      }
      const response = await fetch(route, {
        method: 'POST',
        headers,
        body: JSON.stringify({overlay_configs: overlayConfigs ?? []}),
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const blob = await response.blob();
      return blob;
    },
    async exportGeotiff(
      fId: number,
      sourceId: string,
      layerId: string,
      sensingTime: string,
      filename: string
    ) {
      return apiUtils.makeApiRequest<string>(
        `export/raster/${fId}/${sourceId}/${layerId}/${sensingTime}.tif`,
        {filename}
      );
    },
    async exportGif(
      fId: number,
      scenes: {source_id: string; layer_id: string; sensing_time: string}[],
      overlayConfigs?: OverlayConfig[]
    ) {
      const route = apiUtils.getApiRoute(`export/raster/gif/${fId}`, null, {slowApi: true});

      const auth = getAuth();
      const headers = new Headers();
      headers.append('Content-Type', 'application/json');
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        if (token) {
          headers.append('Authorization', token);
        }
        if (apiUtils.contextOrganizationId) {
          headers.append('x-context-organization-id', apiUtils.contextOrganizationId);
        }
      }
      const response = await fetch(route, {
        method: 'POST',
        headers,
        body: JSON.stringify({scenes, overlay_configs: overlayConfigs ?? []}),
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const blob = await response.blob();
      return blob;
    },
    async exportGeoPdf(
      fId: number,
      sourceId: string,
      layerKey: string,
      sensingTime: string,
      overlayConfigs?: OverlayConfig[]
    ) {
      const route = apiUtils.getApiRoute(
        `export/raster/${fId}/${sourceId}/${layerKey}/${sensingTime}.pdf`,
        null,
        {slowApi: true}
      );

      const auth = getAuth();
      const headers = new Headers();
      headers.append('Content-Type', 'application/json');
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        if (token) {
          headers.append('Authorization', token);
        }
        if (apiUtils.contextOrganizationId) {
          headers.append('x-context-organization-id', apiUtils.contextOrganizationId);
        }
      }
      const response = await fetch(route, {
        method: 'POST',
        headers,
        body: JSON.stringify({overlay_configs: overlayConfigs ?? []}),
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const blob = await response.blob();
      return blob;
    },
    listNotes(
      pId: string,
      fId: number,
      params: apiUtils.ApiParams = {},
      options: apiUtils.ListOptions = {getAllPages: true}
    ) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiNote[]>(`projects/${pId}/${routeBase}/${fId}/notes`, p);

      return makeRequestWithPages(request, params, options);
    },

    createNote(
      pId: string,
      fId: number,
      data: Pick<
        ApiNote,
        | 'text'
        | 'geometry'
        | 'attachments'
        | 'cursorKey'
        | 'cursorType'
        | 'layerKey'
        | 'graph'
        | 'tagIds'
        | 'overlayFeatureCollectionIds'
        | 'properties'
      > & {
        // only include this if you want to manually set the user_id on note create
        userId?: string;
      }
    ) {
      return apiUtils.makeApiRequest<ApiNote>(`projects/${pId}/${routeBase}/${fId}/notes`, null, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    mergeNotes(
      pId: string,
      fId: number,
      data: {
        noteIds: number[];
        text: string;
        noteType: ApiNote['noteType'];
        // only include this if you want to manually set the user_id on the merged note
        userId?: string;
        properties?: ApiNote['properties'];
      }
    ) {
      return apiUtils.makeApiRequest<ApiNote>(
        `projects/${pId}/${routeBase}/${fId}/notes/merge`,
        null,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    patchNote(
      pId: string,
      fId: number,
      noteId: number,
      data: I.MergesInto<I.ImmutableOf<ApiNote>> | Partial<ApiNote>
    ) {
      return apiUtils.makeApiRequest<ApiNote>(
        `projects/${pId}/${routeBase}/${fId}/notes/${noteId}`,
        null,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    summarizeNotes: async function* (
      firebaseToken: string,
      pId: string,
      fId: number,
      noteIds: (string | number)[]
    ) {
      const route = `projects/${pId}/${routeBase}/${fId}/notes/summarize`;
      const url = apiUtils.getApiRoute(route, null, {slowApi: true});
      const headers = new Headers();
      headers.append('Content-Type', 'application/json');
      headers.append('Authorization', firebaseToken);

      if (apiUtils.contextOrganizationId) {
        headers.append('x-context-organization-id', apiUtils.contextOrganizationId);
      }
      const fetchInit = {
        method: 'POST',
        headers,
        body: JSON.stringify({noteIds}),
      };
      const response = await fetch(url, fetchInit);
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      while (true) {
        const {done, value} = await reader!.read();

        acc += decoder.decode(value);
        yield acc;

        if (done) {
          return acc;
        }
      }
    },
  };
}

////////////////////////////////////////////////////////////////////////////////
// REPORTS
////////////////////////////////////////////////////////////////////////////////

function _makeReportsApi(featureId: number) {
  const routeBase = `/features/${featureId}/reports`;
  return {
    get(reportId: number) {
      return apiUtils.makeApiRequest<ApiReport<ReportState>>(`${routeBase}/${reportId}`, null, {
        method: 'GET',
      });
    },

    getList() {
      return apiUtils.makeApiRequest<ApiReport<string>>(routeBase, null, {
        method: 'GET',
      });
    },

    create(report: ReportState, title: string, isMultilocation: boolean) {
      return apiUtils.makeApiRequest<ApiReport<ReportState>>(`${routeBase}`, null, {
        method: 'POST',
        body: JSON.stringify({
          data: report,
          title: title,
          isMultilocation: isMultilocation,
        }),
      });
    },

    update(reportId: number, report: ReportState) {
      return apiUtils.makeApiRequest<ApiReport<ReportState>>(`${routeBase}/${reportId}`, null, {
        method: 'PATCH',
        body: JSON.stringify({
          data: report,
        }),
      });
    },

    updateTitle(reportId: number, newTitle: string) {
      return apiUtils.makeApiRequest<ApiReport<ReportState>>(`${routeBase}/${reportId}`, null, {
        method: 'PATCH',
        body: JSON.stringify({
          title: newTitle,
        }),
      });
    },

    archive(reportId: number) {
      return apiUtils.makeApiRequest<ApiReport<ReportState>>(`${routeBase}/${reportId}`, null, {
        method: 'PATCH',
        body: JSON.stringify({
          isArchived: true,
        }),
      });
    },
  };
}

function _makeProjectReportsApi(projectId: string) {
  const routeBase = `projects/${projectId}/reports`;
  return {
    getList(
      params: apiUtils.ApiParams = {perPage: 100},
      options: apiUtils.ListOptions = {getAllPages: true}
    ) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiProjectReport[]>(routeBase, p);
      return makeRequestWithPages(request, params, options);
    },

    exportPdfs(reportIds: number[]) {
      return apiUtils.makeApiRequest<ApiReportExportRequest>(`${routeBase}/export`, null, {
        method: 'POST',
        body: JSON.stringify({reportIds}),
      });
    },

    batchArchive(reportIds: number[]) {
      return apiUtils.makeApiRequest<ApiReportBatchUpdateResponse>(`${routeBase}/batch`, null, {
        method: 'PATCH',
        body: JSON.stringify({reportIds, isArchived: true}),
      });
    },
  };
}

function _makeShareLinksApi(orgId: string) {
  const routeBase = `p/organizations/${orgId}/shareLinks`;
  return {
    getList(
      params: apiUtils.ApiParams = {perPage: 100},
      options: apiUtils.ListOptions = {getAllPages: true}
    ) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiShareLink[]>(routeBase, p);
      return makeRequestWithPages(request, params, options);
    },

    batchDelete(ids: string[]) {
      return apiUtils.makeApiRequest<ApiShareLinkDeleteResponse>(
        routeBase,
        {id: ids},
        {
          method: 'DELETE',
        }
      );
    },
  };
}

////////////////////////////////////////////////////////////////////////////////
// DASHBOARD
////////////////////////////////////////////////////////////////////////////////

function _makeDashboardStatsApi(orgId: string) {
  const routeBase = `dashboard`;
  return {
    getDashboardConfigs() {
      return apiUtils.makeApiRequest<{[id: string]: DashboardConfig}>(
        `${routeBase}/${orgId}`,
        null,
        {
          method: 'GET',
        }
      );
    },
    getMultiPropertyAnalysis(
      params: Omit<
        MultifeatureAnalysisConfig,
        'title' | 'description' | 'type' | 'id' | 'isPublished'
      > & {
        useAllOrgFeatures?: boolean;
        layerKey: string;
        sourceId: string;
      },
      apiParams: apiUtils.ApiParams = {perPage: 100} // matches the backend default in lens_dashboard.py
    ) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<FeatureStatsApiResponse[]>(
          `${routeBase}/${orgId}/multiPropertyAnalysis`,
          p,
          {
            method: 'POST',
            body: JSON.stringify({
              ...params,
            }),
          }
        );
      return makeRequestWithPages(request, apiParams, {getAllPages: true});
    },
    getFeatures(params: apiUtils.ApiParams = {}) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<FeatureMetadataApiResponse[]>(`${routeBase}/${orgId}/features`, p);
      return makeRequestWithPages(request, params, {getAllPages: false});
    },
    getNotes(params: apiUtils.ApiParams = {}, options: apiUtils.ListOptions = {getAllPages: true}) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiNote[]>(`${routeBase}/${orgId}/notes`, p);
      return makeRequestWithPages(request, params, options);
    },
    getReports(
      params: apiUtils.ApiParams = {},
      options: apiUtils.ListOptions = {getAllPages: true}
    ) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiReport<string>[]>(`${routeBase}/${orgId}/reports`, p);
      return makeRequestWithPages(request, params, options);
    },
    shareWidget(
      widgetId: number,
      displayOptions: MultifeatureAnalysisConfig['displayOptions'] = {}
    ) {
      return apiUtils.makeApiRequest<{widgetId: string; configId: string}>(
        `${routeBase}/${orgId}/widgets/${widgetId}/share`,
        undefined,
        {
          method: 'POST',
          body: JSON.stringify({displayOptions}),
        }
      );
    },
  };
}

////////////////////////////////////////////////////////////////////////////////
// ORGANIZATIONS
////////////////////////////////////////////////////////////////////////////////

function _makeOrganizationsApi() {
  return {
    ...apiUtils._createApiResource<ApiOrganization, string, never>('organizations'),

    users(orgId: string, params: apiUtils.ApiParams = {}) {
      return apiUtils.makeApiRequest<ApiOrganizationUser[]>(
        `organizations/${orgId}/users`,
        params,
        undefined
      );
    },

    signupTokens(orgId: string, params: apiUtils.ApiParams = {}) {
      return apiUtils.makeApiRequest<ApiSignupToken[]>(
        `organizations/${orgId}/signup_tokens`,
        params,
        undefined
      );
    },

    contracts(orgId: string, params: apiUtils.ApiParams = {}) {
      return apiUtils.makeApiRequest<ApiImageryContractWithProjects[]>(
        `organizations/${orgId}/contracts`,
        params,
        undefined
      );
    },

    /** Null when the organization isn't on budgets, so each contract's own NTE applies. */
    currentImageryBudget(orgId: string) {
      return apiUtils.makeApiRequest<ApiImageryBudget | null>(
        `organizations/${orgId}/imagery_budgets/current`
      );
    },

    agentRunCount(orgId: string) {
      return apiUtils.makeApiRequest<number>(`organizations/${orgId}/agent_run_count`, null, {
        method: 'GET',
      });
    },

    invoices: (orgId: string) => {
      return apiUtils.makeApiRequest<ApiInvoice[]>(
        `imagery/imagery_contracts/${orgId}/invoices`,
        null,
        {
          method: 'GET',
        }
      );
    },

    upcomingInvoice(orgId: string, subscriptionId: string, params: apiUtils.ApiParams = {}) {
      return apiUtils.makeApiRequest<ApiImageryContract[]>(
        `organizations/${orgId}/subscriptions/${subscriptionId}/upcoming_invoice`,
        params
      );
    },

    accessSubscription(orgId: string, params: apiUtils.ApiParams = {}) {
      return apiUtils.makeApiRequest<ApiAccessSubscription>(
        `organizations/${orgId}/access_subscription`,
        params,
        undefined
      );
    },

    async deleteUser(userId: string) {
      await apiUtils.makeApiRequest<unknown>(`user/${userId}`, {}, {method: 'DELETE'});
    },

    setUserRole(userId: string, role: ApiUserRoleValue['role']) {
      const changes: ApiUserRoleValue = {user_id: userId, role};

      return apiUtils.makeApiRequest<ApiUserRoleValue>(
        `user/update_role`,
        {},
        {
          method: 'PATCH',
          body: JSON.stringify(changes),
        }
      );
    },

    // While we currently only support updates to the user's name, this method
    // may be extended to update other properties in the future.
    updateUser(userId: string, update: Pick<ApiOrganizationUser, 'name'>) {
      return apiUtils.makeApiRequest<ApiOrganizationUser>(
        `user/update_profile`,
        {},
        {
          method: 'PATCH',
          body: JSON.stringify({user_id: userId, ...update}),
        }
      );
    },

    updateEmail(email: string) {
      return apiUtils.makeApiRequest<ApiOrganizationUser>(
        `user/update_email`,
        {},
        {
          method: 'PATCH',
          body: JSON.stringify({email}),
        }
      );
    },

    // Note that settingsUpdate is updated shallowly - keep settings keys not too deeply nested to limit complexity
    updateSettings(settingsUpdate: Partial<ApiOrganizationUser['settings']>) {
      return apiUtils.makeApiRequest<Pick<ApiOrganizationUser, 'id' | 'settings'>>(
        'user/update_settings',
        {},
        {method: 'PATCH', body: JSON.stringify(settingsUpdate)}
      );
    },

    getHubspotToken() {
      return apiUtils.makeApiRequest<{token: string}>(`user/hubspot_token`);
    },

    childOrganizations(orgId: string, params: apiUtils.ApiParams = {}) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiOrganization[]>(
          `organizations/${orgId}/children`,
          p,
          {},
          {noContextOrg: true}
        );

      return makeRequestWithPages(request, params, {getAllPages: true});
    },

    setLogo(orgId: string, file?: File) {
      const formData = new FormData();
      formData.append('logo', file || '');

      return apiUtils.makeApiRequest<ApiOrganization>(`organizations/${orgId}/logo`, null, {
        method: 'POST',
        body: formData,
      });
    },

    getOverlays(orgId: string, params: apiUtils.ApiParams = {}) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiOverlay[]>(
          `organizations/${orgId}/overlays`,
          p,
          {
            method: 'GET',
          },
          {}
        );
      return makeRequestWithPages(request, params, {});
    },

    listPremiumSources(orgId: string, params: apiUtils.ApiParams = {}) {
      return apiUtils.makeApiRequest<ApiListPremiumSourcesResponse>(
        `organizations/${orgId}/premium_sources`,
        params,
        {
          method: 'GET',
        },
        {}
      );
    },
    previewPaidLayerInvoice(
      orgId: string,
      sourceId: string,
      billingInterval: string,
      params: apiUtils.ApiParams = {}
    ) {
      return apiUtils.makeApiRequest<ApiInvoice>(
        `organizations/${orgId}/paid_layer/${sourceId}/${billingInterval}`,
        params,
        {
          method: 'GET',
        },
        {}
      );
    },
    addPaidLayer(
      orgId: string,
      sourceId: string,
      billingInterval: string,
      startDate: number,
      billingCycleAnchor: number,
      couponId: string | undefined = undefined,
      params: apiUtils.ApiParams = {}
    ) {
      return apiUtils.makeApiRequest<{clientSecret: string}>(
        `organizations/${orgId}/paid_layer/${sourceId}/${billingInterval}`,
        params,
        {
          method: 'POST',
          body: JSON.stringify({
            startDate,
            billingCycleAnchor,
            couponId,
          }),
        },
        {}
      );
    },
    // This session url expires after 5 min, make this request on user click to be safe
    // and avoid having an expired url
    getBillingSession(orgId: string, returnUrl: string, params: apiUtils.ApiParams = {}) {
      return apiUtils.makeApiRequest<ApiBillingSessionResponse>(
        `organizations/${orgId}/billingSession`,
        params,
        {
          method: 'POST',
          body: JSON.stringify({
            returnUrl,
          }),
        },
        {}
      );
    },
    /**
     * In-app card management, replacing what the Stripe Billing Portal used to do. Every route is
     * owner/admin and 402s when the organization has no Stripe customer.
     */
    paymentMethods: {
      createCustomerSession: async (orgId: string): Promise<ApiStripeClientSecret> => {
        const response = await apiUtils.makeApiRequest<ApiStripeClientSecret>(
          `organizations/${orgId}/paymentMethods/customerSession`,
          null,
          {method: 'POST'}
        );
        return response.get('data').toJS() as ApiStripeClientSecret;
      },

      createSetupIntent: async (orgId: string): Promise<ApiStripeClientSecret> => {
        const response = await apiUtils.makeApiRequest<ApiStripeClientSecret>(
          `organizations/${orgId}/paymentMethods/setupIntent`,
          null,
          {method: 'POST'}
        );
        return response.get('data').toJS() as ApiStripeClientSecret;
      },

      list: async (orgId: string): Promise<ApiPaymentMethod[]> => {
        const response = await apiUtils.makeApiRequest<{items: ApiPaymentMethod[]}>(
          `organizations/${orgId}/paymentMethods`,
          null,
          {method: 'GET'}
        );
        return response.getIn(['data', 'items']).toJS() as ApiPaymentMethod[];
      },

      remove: async (orgId: string, paymentMethodId: string): Promise<void> => {
        await apiUtils.makeApiRequest<void>(
          `organizations/${orgId}/paymentMethods/${paymentMethodId}`,
          null,
          {method: 'DELETE'}
        );
      },

      setDefault: async (orgId: string, paymentMethodId: string): Promise<void> => {
        await apiUtils.makeApiRequest<void>(
          `organizations/${orgId}/paymentMethods/${paymentMethodId}/default`,
          null,
          {method: 'POST'}
        );
      },
    },
    /** Owners and admins get the whole organization's card purchases, regular users only their own. */
    cardPurchases: {
      list: async (orgId: string, page: number, perPage: number): Promise<ApiCardPurchase[]> => {
        const response = await apiUtils.makeApiRequest<ApiCardPurchase[]>(
          `organizations/${orgId}/imageryCheckouts`,
          {page, perPage},
          {method: 'GET'}
        );
        return response.get('data').toJS() as ApiCardPurchase[];
      },

      getInvoice: async (orgId: string, checkoutKey: string): Promise<ApiCardPurchaseInvoice> => {
        const response = await apiUtils.makeApiRequest<ApiCardPurchaseInvoice>(
          `organizations/${orgId}/imageryCheckouts/${encodeURIComponent(checkoutKey)}/invoice`,
          null,
          {method: 'GET'}
        );
        return response.get('data').toJS() as ApiCardPurchaseInvoice;
      },
    },
    /** Newest first, including credit that's been used up. */
    imageryPrepaids: async (orgId: string): Promise<ApiImageryPrepaid[]> => {
      const response = await apiUtils.makeApiRequest<ApiImageryPrepaid[]>(
        `organizations/${orgId}/imagery_prepaids`,
        null,
        {method: 'GET'}
      );
      return response.get('data').toJS() as ApiImageryPrepaid[];
    },
    getBillingInfo(orgId: string, params: apiUtils.ApiParams = {}) {
      return apiUtils.makeApiRequest<ApiDefaultPaymentMethod>(
        `organizations/${orgId}/billingInfo`,
        params,
        {
          method: 'GET',
        },
        {}
      );
    },
    cancelSubscription(
      orgId: string,
      subscriptionId: string,
      comment: string,
      params: apiUtils.ApiParams = {}
    ) {
      return apiUtils.makeApiRequest(
        `organizations/${orgId}/subscription/${subscriptionId}`,
        params,
        {
          method: 'DELETE',
          body: JSON.stringify({
            comment,
          }),
        },
        {}
      );
    },
    listNotifications(
      orgId: string,
      params: apiUtils.ApiParams = {},
      options: apiUtils.ListOptions = {getAllPages: true}
    ) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiNotification[]>(`organizations/${orgId}/notificationEvents`, p);

      return makeRequestWithPages(request, params, options);
    },
    assignedFeatures(
      orgId: string,
      params: apiUtils.ApiParams = {},
      options: apiUtils.ListOptions = {getAllPages: true}
    ) {
      const request = (p: apiUtils.ApiParams) =>
        apiUtils.makeApiRequest<ApiFeature[]>(`organizations/${orgId}/assignedFeatures`, p);

      return makeRequestWithPages(request, params, options);
    },
    invite(
      orgId: string,
      email: string,
      role: ApiUserRoleValue['role'],
      params: apiUtils.ApiParams = {}
    ) {
      return apiUtils.makeApiRequest<void>(`organizations/${orgId}/invite`, params, {
        method: 'POST',
        body: JSON.stringify({emails: [email], role, product: CONSTANTS.ORG_PRODUCT_LENS}),
      });
    },
    workflows(orgId: string, params: apiUtils.ApiParams = {}) {
      return apiUtils.makeApiRequest<ApiAgentWorkflow[]>(
        `organizations/${orgId}/workflows`,
        params
      );
    },
  };
}

////////////////////////////////////////////////////////////////////////////////
// ADMIN
////////////////////////////////////////////////////////////////////////////////

export interface StripeContractDescription {
  isTest: boolean;
  organizationId: string;
  stripeCustomerId: string;

  startDateYyyyMmDd: string;
  endDateYyyyMmDd: string | null;
  accessSubscriptionType: CONSTANTS.LENS_ACCESS_SUBSCRIPTIONS_TYPE;

  createAccessSubscription: boolean;
  customAccessSubscriptionPrice: null | {
    amountCents: number;
    interval: 'day' | 'week' | 'month' | 'year';
    intervalCount: number;
  };

  accessSubscriptionRecurringItems: {
    addon: CONSTANTS.LENS_ADDONS_TYPE;
    amountCents: number;
  }[];

  accessSubscriptionOneTimeItems: {
    description: string;
    amountCents: number;
  }[];

  createImagerySubscription: boolean;
  imageryContractName: string;
  imageryPrepaidDollars: string;
  imageryNteDollars: string;
  imageryProjectIds: string[];
}

function makeRequestWithPages<T>(
  request: (p: apiUtils.ApiParams) => Promise<apiUtils.ApiResponse<T[]>>,
  params: apiUtils.ApiParams,
  options: apiUtils.ListOptions
) {
  if (options.getAllPages) {
    return apiUtils.getAllPages(
      request,
      params,
      typeof options.getAllPages === 'number' ? options.getAllPages : undefined
    );
  } else {
    return request(params);
  }
}
